import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { BookOpenCheck, Check, Eye } from "lucide-react";
import {
  type SharedDocumentDetails,
  type SharedDocumentDetailsVersion,
} from "~/api/types";
import {
  addSharedReaders,
  adoptSharedVersion,
  hasReadShared,
} from "~/api/v2/requests";
import useUsers from "~/api/swr/useUsers";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { Card, CardContent } from "~/atoms/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import { PlatformRole, UserStatus } from "~/config/types";
import { formatDate } from "~/helpers/date";
import { printFullName } from "~/helpers/user";
import { useUserContext } from "~/hooks/useUserContext";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import { MetadataSection } from "~/molecules/DocumentMetadataSections";

interface Props {
  document: SharedDocumentDetails;
  version: SharedDocumentDetailsVersion;
  onChange: () => void;
}

export const SharedDocumentPanel = ({ document, version, onChange }: Props) => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  const canManage =
    user.role === PlatformRole.superAdmin ||
    user.role === PlatformRole.coordinator;
  const isLatestPublished = version.id === document.latestPublishedVersionId;
  const currentReader = version.readers.find(
    (reader) => reader.userId === user.id,
  );

  const adopt = async () => {
    try {
      await adoptSharedVersion(version.id);
      onChange();
      toast.success(t(($) => $.sharedDocuments.documentAdopted));
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    }
  };

  const markAsRead = async () => {
    try {
      await hasReadShared(version.id, user.id);
      onChange();
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <MetadataSection title={t(($) => $.sharedDocuments.sharedBy)}>
          <div className="flex items-center justify-between">
            <span className="text-delegation text-sm font-medium">
              {document.sharedBy.name}
            </span>
            <span className="text-muted-foreground text-sm">
              {formatDate(document.sharedAt)}
            </span>
          </div>
        </MetadataSection>

        <MetadataSection
          title={t(($) => $.sharedDocuments.adopted)}
          action={
            canManage &&
            isLatestPublished &&
            !version.adoption && (
              <Button size="sm" onClick={() => void adopt()}>
                {t(($) => $.sharedDocuments.adopt)}
              </Button>
            )
          }
        >
          {version.adoption ? (
            <div className="flex items-center gap-2">
              <Badge variant="success">
                {t(($) => $.sharedDocuments.adopted)}
              </Badge>
              <span className="text-muted-foreground text-sm">
                {t(($) => $.sharedDocuments.adoptedDetails, {
                  date: formatDate(version.adoption.adoptedAt),
                  name: version.adoption.adoptedBy
                    ? printFullName(version.adoption.adoptedBy)
                    : "-",
                })}
              </span>
            </div>
          ) : (
            <Badge variant="destructive">
              {t(($) => $.sharedDocuments.notAdopted)}
            </Badge>
          )}
        </MetadataSection>

        <MetadataSection
          title={t(($) => $.sharedDocuments.readersTitle)}
          action={
            canManage &&
            isLatestPublished && (
              <InviteSharedReadersDialog
                version={version}
                onInvited={onChange}
              />
            )
          }
        >
          <div className="text-muted-foreground flex gap-3 text-sm">
            <span>
              {t(($) => $.sharedDocuments.invited)}:{" "}
              {version.readerSummary.totalCount}
            </span>
            <span>
              {t(($) => $.sharedDocuments.opened)}:{" "}
              {version.readerSummary.openedCount}
            </span>
            <span>
              {t(($) => $.sharedDocuments.read)}:{" "}
              {version.readerSummary.readCount}
            </span>
          </div>

          {version.readers.length > 0 && (
            <ul className="flex flex-col gap-1">
              {version.readers.map((reader) => (
                <li
                  key={reader.userId}
                  className="flex items-center justify-between text-sm"
                >
                  <span>{printFullName(reader)}</span>
                  <span className="text-muted-foreground flex items-center gap-1">
                    {reader.hasOpened && <Eye className="size-4" />}
                    {reader.hasRead && <Check className="size-4" />}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {currentReader && !currentReader.hasRead && (
            <Button variant="outline" onClick={() => void markAsRead()}>
              <BookOpenCheck />
              {t(($) => $.sharedDocuments.markAsRead)}
            </Button>
          )}
        </MetadataSection>
      </CardContent>
    </Card>
  );
};

const InviteSharedReadersDialog = ({
  version,
  onInvited,
}: {
  version: SharedDocumentDetailsVersion;
  onInvited: () => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const { data: users } = useUsers({ status: [UserStatus.active] });

  const invitedIds = new Set(
    version.readers.map((reader) => reader.userId.toString()),
  );
  const options = (users ?? []).map((user) => ({
    value: user.id.toString(),
    label: printFullName(user),
    subtitle: user.email,
    disabled: invitedIds.has(user.id.toString()),
  }));

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setSelectedUserIds([]);
    }
    setOpen(nextOpen);
  };

  const submit = async () => {
    try {
      setIsSubmitting(true);
      await addSharedReaders(version.id, selectedUserIds.map(Number));
      onInvited();
      toast.success(t(($) => $.toasts.success.readersInvited));
      setOpen(false);
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {t(($) => $.sharedDocuments.inviteReaders)}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-100">
        <DialogHeader>
          <DialogTitle>{t(($) => $.sharedDocuments.inviteReaders)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.sharedDocuments.inviteReadersDescription)}
          </DialogDescription>
        </DialogHeader>
        <ControlledCombobox
          isMulti
          popoverModal
          options={options}
          value={selectedUserIds}
          onChange={setSelectedUserIds}
        />
        <DialogFooter>
          <Button
            onClick={() => void submit()}
            disabled={isSubmitting || selectedUserIds.length === 0}
          >
            {t(($) => $.commons.buttons.sentInvitations)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
